import React, { useState, useMemo, useEffect } from "react";
import { useAdminPayroll } from "@/hooks/useAdminPayroll";
import * as propertyService from "@/lib/services/propertyService";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Users,
  Banknote,
  Loader2,
  Save,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import {
  deletePayrollData,
  getPayrollData,
  getPayrollSummary,
  PayrollFullData,
  savePayrollData,
} from "@/hooks/payrollDataService";

type ServiceStyle = "efficient" | "balanced" | "luxury";

interface PositionState {
  positionId: string;
  positionName: string;
  departmentId: string;
  included: boolean;
  calculatedFTEs: number;
  monthlySalary: number;
  overrideFTEs: number | null;
  overrideSalary: number | null;
}

interface PayrollState {
  marketId: string;
  serviceStyle: ServiceStyle;
  socialChargesPercent: number;
  positions: PositionState[];
}

interface PayrollTotals {
  totalFTEs: string;
  monthlyPayroll: number;
  socialCharges: number;
  totalMonthly: number;
  annualCost: number;
}

interface SavedPayrollData {
  rooms: number;
  totals: PayrollTotals;
  savedAt: string;
  marketId: string;
  positions: PositionState[];
  serviceStyle: ServiceStyle;
  socialChargesPercent: number;
}

interface PayrollTabProps {
  dealId?: string;
  rooms?: number;
  currency?: string;
}

export default function PayrollTab({
  dealId,
  rooms = 120,
  currency = "€",
}: PayrollTabProps) {
  const {
    departments,
    positions,
    markets,
    salaries,
    fteRules,
    loading: adminLoading,
    refreshData,
  } = useAdminPayroll();

  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [expandedDepts, setExpandedDepts] = useState<string[]>([]);
  const [payrollData, setPayrollData] = useState<PayrollState>({
    marketId: "",
    serviceStyle: "balanced",
    socialChargesPercent: 0,
    positions: [],
  });

  const [payrollDataId, setPayrollDataId] = useState<string | null>(null);

  useEffect(() => {
    if (dealId) {
      loadSavedPayroll();
    } else {
      setLoadingData(false);
    }
  }, [dealId]);

  const loadSavedPayroll = async () => {
    if (!dealId) return;

    try {
      setLoadingData(true);

      const payrollSummary = await getPayrollSummary(dealId);

      if (payrollSummary) {
        setPayrollDataId(payrollSummary.dataId);
        setLastSaved(payrollSummary.lastUpdated);

        const fullData = await getPayrollData(dealId);
        if (fullData) {
          setPayrollData({
            marketId: fullData.marketId,
            serviceStyle: fullData.serviceStyle,
            socialChargesPercent: fullData.socialChargesPercent,
            positions: fullData.positions,
          });
        }
      } else {
        const property = await propertyService.getPropertyById(dealId);
        const saved = property?.kpis?.payroll;

        if (saved && saved.positions) {
          const payroll = saved as SavedPayrollData;

          setPayrollData({
            marketId: payroll.marketId,
            serviceStyle: payroll.serviceStyle,
            socialChargesPercent: payroll.socialChargesPercent,
            positions: payroll.positions,
          });

          setLastSaved(payroll.savedAt);

          await migrateOldPayrollData(dealId, payroll);
        }
      }
    } catch (error) {
      console.error("Failed to load payroll:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const migrateOldPayrollData = async (
    propertyId: string,
    oldData: SavedPayrollData,
  ) => {
    try {
      const migratedData: PayrollFullData = {
        rooms: oldData.rooms || rooms,
        totals: oldData.totals,
        savedAt: oldData.savedAt,
        marketId: oldData.marketId,
        positions: oldData.positions,
        serviceStyle: oldData.serviceStyle,
        socialChargesPercent: oldData.socialChargesPercent,
      };

      const record = await savePayrollData(propertyId, migratedData);
      setPayrollDataId(record.id);

      await cleanupOldPayrollData(propertyId);
    } catch (error) {
      console.error("Failed to migrate old payroll data:", error);
    }
  };

  const cleanupOldPayrollData = async (propertyId: string) => {
    try {
      const currentDoc = await propertyService.getPropertyById(propertyId);
      if (currentDoc?.kpis?.payroll) {
        const summary = await getPayrollSummary(propertyId);
        if (summary) {
          await propertyService.updateProperty(propertyId, {
            kpis: {
              ...currentDoc.kpis,
              payroll: {
                dataId: summary.dataId,
                totalAnnual: summary.totalAnnual,
                lastUpdated: summary.lastUpdated,
              },
            },
          });
        }
      }
    } catch (error) {
      console.error("Failed to cleanup old payroll data:", error);
    }
  };

  const handleSavePayroll = async () => {
    if (!dealId) return;
    if (!payrollData.marketId) {
      toast.error("Please select a labor market first");
      return;
    }

    setSaving(true);

    try {
      const payrollToSave: SavedPayrollData = {
        rooms,
        totals: payrollTotals,
        savedAt: new Date().toISOString(),
        marketId: payrollData.marketId,
        positions: payrollData.positions,
        serviceStyle: payrollData.serviceStyle,
        socialChargesPercent: payrollData.socialChargesPercent,
      };

      const savedRecord = await savePayrollData(dealId, payrollToSave);
      setPayrollDataId(savedRecord.id);

      const summary = await getPayrollSummary(dealId);
      if (!summary) {
        throw new Error("Failed to get payroll summary");
      }

      const currentDoc = await propertyService.getPropertyById(dealId);
      await propertyService.updateProperty(dealId, {
        kpis: {
          ...(currentDoc?.kpis || {}),
          payroll: {
            dataId: summary.dataId,
            totalAnnual: summary.totalAnnual,
            totalMonthly: summary.totalMonthly,
            totalFTEs: summary.totalFTEs,
            lastUpdated: summary.lastUpdated,
          },
          totalPayrollAnnual: summary.totalAnnual,
        },
      });

      setLastSaved(payrollToSave.savedAt);
      toast.success("Payroll updated and optimized");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to update payroll");
    } finally {
      setSaving(false);
    }
  };

  const calculatePayroll = (marketId: string, style: ServiceStyle) => {
    const selectedMarket = markets.find((m) => m.id === marketId);
    if (!selectedMarket) return;

    const calculatedPositions: PositionState[] = positions
      .filter((p) => p.is_active)
      ?.map((pos) => {
        const rule = fteRules.find(
          (r) => r.position_id === pos.id && r.is_active,
        );
        let ftes = 0;

        if (rule) {
          if (rule.per_rooms && rule.per_rooms > 0) {
            ftes = rooms / rule.per_rooms;
          }
          ftes = Math.max(ftes, rule.min_fte || 0);
        }

        const salaryEntry = salaries.find(
          (s) => s.position_id === pos.id && s.market_id === marketId,
        );

        let monthlySalary = 0;
        if (salaryEntry) {
          if (style === "efficient")
            monthlySalary = salaryEntry.salary_efficient;
          else if (style === "balanced")
            monthlySalary = salaryEntry.salary_balanced;
          else if (style === "luxury")
            monthlySalary = salaryEntry.salary_luxury;
        }

        return {
          positionId: pos.id,
          positionName: pos.name,
          departmentId: pos.department_id,
          included: ftes > 0,
          calculatedFTEs: Number(ftes?.toFixed(2)),
          monthlySalary: monthlySalary,
          overrideFTEs: null,
          overrideSalary: null,
        };
      });

    setPayrollData({
      marketId,
      serviceStyle: style,
      socialChargesPercent: selectedMarket.social_charges_percent || 0,
      positions: calculatedPositions,
    });
  };

  const togglePositionIncluded = (id: string) => {
    setPayrollData((prev) => ({
      ...prev,
      positions: prev.positions?.map((p) =>
        p.positionId === id ? { ...p, included: !p.included } : p,
      ),
    }));
  };

  const updatePositionOverride = (
    id: string,
    field: "overrideFTEs" | "overrideSalary",
    value: number | null,
  ) => {
    setPayrollData((prev) => ({
      ...prev,
      positions: prev.positions?.map((p) =>
        p.positionId === id ? { ...p, [field]: value } : p,
      ),
    }));
  };

  const payrollTotals = useMemo(() => {
    const active = payrollData?.positions?.filter((p) => p.included);
    const monthlyPayroll = active?.reduce(
      (sum, p) =>
        sum +
        (p.overrideFTEs ?? p.calculatedFTEs) *
          (p.overrideSalary ?? p.monthlySalary),
      0,
    );
    const totalFTEs = active?.reduce(
      (sum, p) => sum + (p.overrideFTEs ?? p.calculatedFTEs),
      0,
    );
    const socialCharges =
      monthlyPayroll * (payrollData.socialChargesPercent / 100);

    return {
      totalFTEs: totalFTEs?.toFixed(1),
      monthlyPayroll,
      socialCharges,
      totalMonthly: monthlyPayroll + socialCharges,
      annualCost: (monthlyPayroll + socialCharges) * 12,
    };
  }, [payrollData]);

  const handleClearPayroll = async () => {
    if (!dealId) return;

    const toastId = toast.custom(
      (t) => (
        <div className="bg-white rounded-lg shadow-lg p-4 border border-gray-200 min-w-[300px]">
          <p className="text-gray-800 mb-3 font-medium">Clear Payroll Data</p>
          <p className="text-gray-600 text-sm mb-4">
            Are you sure you want to clear payroll data? This action cannot be
            undone.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => toast.dismiss(toastId)}
              className="px-3 py-1.5 bg-gray-200 text-gray-800 text-sm rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                toast.dismiss(toastId);
                clearPayrollConfirmed();
              }}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
      },
    );
  };

  const clearPayrollConfirmed = async () => {
    if (!dealId) return;

    setSaving(true);

    try {
      await deletePayrollData(dealId);

      const currentDoc = await propertyService.getPropertyById(dealId);
      await propertyService.updateProperty(dealId, {
        kpis: {
          ...(currentDoc?.kpis || {}),
          payroll: null,
        },
      });

      setPayrollData({
        marketId: "",
        serviceStyle: "balanced",
        socialChargesPercent: 0,
        positions: [],
      });
      setPayrollDataId(null);
      setLastSaved(null);

      toast.success("Payroll cleared");
    } catch (error) {
      console.error("Failed to clear payroll:", error);
      toast.error("Failed to clear payroll");
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = () => {
    if (payrollData.marketId) {
      calculatePayroll(payrollData.marketId, payrollData.serviceStyle);
      toast.success("Payroll recalculated");
    }
  };

  if ((adminLoading && departments?.length === 0) || loadingData) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <TabsContent value="payroll" className="space-y-6">
      <div className="space-y-6 pt-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Banknote className="h-4 w-4 text-primary" />
                Payroll Configuration
                {lastSaved && (
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Saved {new Date(lastSaved).toLocaleDateString()}
                  </Badge>
                )}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRecalculate}
                  disabled={!payrollData.marketId || saving}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${saving ? "animate-spin" : ""}`}
                  />
                  Recalculate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearPayroll}
                  disabled={!payrollData.marketId || saving}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                <Button
                  size="sm"
                  className="bg-primary-blue"
                  onClick={handleSavePayroll}
                  disabled={!payrollData.marketId || saving}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Payroll
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Labor Market</Label>
                <Select
                  value={payrollData.marketId}
                  onValueChange={(v) =>
                    calculatePayroll(v, payrollData.serviceStyle)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select market..." />
                  </SelectTrigger>
                  <SelectContent>
                    {markets
                      ?.filter((m) => m.is_active)
                      ?.map((market) => (
                        <SelectItem key={market.id} value={market.id}>
                          {market.flag_emoji} {market.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Service Style</Label>
                <Select
                  value={payrollData.serviceStyle}
                  onValueChange={(v) =>
                    payrollData.marketId &&
                    calculatePayroll(payrollData.marketId, v as ServiceStyle)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efficient">
                      Efficient (Low FTE)
                    </SelectItem>
                    <SelectItem value="balanced">
                      Balanced (Standard)
                    </SelectItem>
                    <SelectItem value="luxury">
                      Luxury (High FTE/Salary)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Social Charges (%)</Label>
                <Input
                  type="number"
                  value={payrollData.socialChargesPercent}
                  onChange={(e) =>
                    setPayrollData((prev) => ({
                      ...prev,
                      socialChargesPercent: Number(e.target.value),
                    }))
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {departments
            ?.filter((d) => d.is_active)
            ?.map((dept) => {
              const deptPositions = payrollData?.positions?.filter(
                (p) => p.departmentId === dept.id,
              );
              if (deptPositions?.length === 0) return null;

              const isExpanded = expandedDepts.includes(dept.id);
              const activePositions = deptPositions?.filter((p) => p.included);

              const deptFTEs = activePositions?.reduce(
                (sum, p) => sum + (p.overrideFTEs ?? p.calculatedFTEs),
                0,
              );
              const deptTotal = activePositions?.reduce(
                (sum, p) =>
                  sum +
                  (p.overrideFTEs ?? p.calculatedFTEs) *
                    (p.overrideSalary ?? p.monthlySalary),
                0,
              );

              return (
                <Collapsible
                  key={dept.id}
                  open={isExpanded}
                  onOpenChange={(open) => {
                    setExpandedDepts((prev) =>
                      open
                        ? [...prev, dept.id]
                        : prev?.filter((id) => id !== dept.id),
                    );
                  }}
                >
                  <Card className="overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors border-b">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-1 h-6 rounded-full"
                            style={{ backgroundColor: dept.color }}
                          />
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span className="font-semibold text-sm">
                            {dept.name}
                          </span>
                          <Badge
                            variant="secondary"
                            className="text-[10px] h-5"
                          >
                            {activePositions?.length} active
                          </Badge>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {deptFTEs?.toFixed(1)}
                            </span>{" "}
                            FTEs
                          </span>
                          <span className="font-bold text-primary">
                            {currency}
                            {Math.round(deptTotal)?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <Table>
                        <TableHeader className="bg-muted/30">
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Position</TableHead>
                            <TableHead className="text-center w-20">
                              Calc. FTE
                            </TableHead>
                            <TableHead className="text-center w-24">
                              Override
                            </TableHead>
                            <TableHead className="text-right w-28">
                              Base Salary
                            </TableHead>
                            <TableHead className="text-right w-28">
                              Override
                            </TableHead>
                            <TableHead className="text-right w-32">
                              Total Cost
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deptPositions?.map((pos) => {
                            const ftes = pos.overrideFTEs ?? pos.calculatedFTEs;
                            const salary =
                              pos.overrideSalary ?? pos.monthlySalary;
                            const cost = ftes * salary;

                            return (
                              <TableRow
                                key={pos.positionId}
                                className={!pos.included ? "opacity-40" : ""}
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={pos.included}
                                    onCheckedChange={() =>
                                      togglePositionIncluded(pos.positionId)
                                    }
                                  />
                                </TableCell>
                                <TableCell className="font-medium text-xs">
                                  {pos.positionName}
                                </TableCell>
                                <TableCell className="text-center text-xs text-muted-foreground">
                                  {pos.calculatedFTEs}
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    className="h-7 w-16 text-center mx-auto text-xs"
                                    value={pos.overrideFTEs ?? ""}
                                    onChange={(e) =>
                                      updatePositionOverride(
                                        pos.positionId,
                                        "overrideFTEs",
                                        e.target.value
                                          ? Number(e.target.value)
                                          : null,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell className="text-right text-[11px] text-muted-foreground">
                                  {currency}
                                  {pos.monthlySalary.toLocaleString()}
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    className="h-7 w-24 text-right ml-auto text-xs"
                                    value={pos.overrideSalary ?? ""}
                                    onChange={(e) =>
                                      updatePositionOverride(
                                        pos.positionId,
                                        "overrideSalary",
                                        e.target.value
                                          ? Number(e.target.value)
                                          : null,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell className="text-right font-bold text-xs">
                                  {pos.included
                                    ? `${currency}${Math.round(cost).toLocaleString()}`
                                    : "-"}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              );
            })}
        </div>

        {payrollData.positions?.length > 0 && (
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <div className="grid grid-cols-5 gap-4 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Total FTEs</p>
                  <p className="text-xl font-bold">{payrollTotals.totalFTEs}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    Monthly Payroll
                  </p>
                  <p className="text-xl font-bold">
                    {currency} {payrollTotals.monthlyPayroll.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    Social Charges ({payrollData.socialChargesPercent}%)
                  </p>
                  <p className="text-xl font-bold">
                    {currency}{" "}
                    {Math.round(payrollTotals.socialCharges).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Monthly</p>
                  <p className="text-xl font-bold text-primary">
                    {currency}{" "}
                    {Math.round(payrollTotals.totalMonthly).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Annual Cost</p>
                  <p className="text-xl font-bold">
                    {currency}{" "}
                    {Math.round(payrollTotals.annualCost).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {payrollData.positions?.length === 0 && (
          <div className="text-center py-20 border-2 border-dashed rounded-xl bg-muted/20">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No payroll data calculated</p>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto mt-1">
              Select a labor market and service style above to generate the team
              composition based on {rooms} rooms.
            </p>
          </div>
        )}
      </div>
    </TabsContent>
  );
}
